<template>
  <div class="content-page">
    <TheBreadcrumb :pages="arianeRoute" />
    <HeaderTournament :page="header" />
    <SecondaryNav
      v-if="nav.length"
      :items="nav"
    />
    <ContestIntro
      :infos="recordContest"
      :logged-in="authenticated"
      contest-type="contest"
      :is-record-contest="true"
    />
    <div
      v-if="recordContest?.gifts?.length"
      ref="a-gagner"
    >
      <RewardsList
        :infos="recordContest"
        :logged-in="authenticated"
        contest-type="contest"
        :is-record-contest="true"
      />
    </div>
    <div
      v-if="hasArticle"
      ref="presentation"
    >
      <ContestArticle :page="article" />
    </div>
    <div
      v-if="recordContest?.how_to?.length && countdown(endDate) >= 0"
      ref="comment-participer"
      :class="{ hasNoArticle: !hasArticle }"
    >
      <ContestRules :rules="recordContest.how_to" />
    </div>
    <ContestPartner :logged-in="authenticated" />
    <div ref="ranking">
      <ContestRanking />
    </div>
    <div id="comments">
      <ContestSocial
        v-if="recordContest?.hide_comments === false"
        :page="contestSocial"
      />
    </div>
    <DoNotMissSection
      v-if="doNotMiss.length"
      :slider-items="doNotMiss"
    />
    <ContestParticipationPopin
      v-if="displayParticipation"
      @change-display="handleChangeDisplay"
    />
  </div>
</template>

<script setup lang="ts">
import {
  activityWordingKey,
  injectedFetchPostKey,
  injectedInvitationApproveKey,
  injectedInvitationDisapproveKey,
  isEndedContestKey,
  isImageContestKey,
  isLockedRankingKey,
  isRunContestKey,
  isRunSecondsContestKey,
  isScoreContestKey,
  isSoloContestKey,
  isVideoContestKey,
} from '@/utils/provideKeys'
import { isBefore } from 'date-fns'

definePageMeta({
  middleware: async (to) => {
    const loadContest = useLoadRecordContest()
    return loadContest(to)
  },
})

const recordContestsStore = useRecordContestsStore()
const { recordContest } = storeToRefs(recordContestsStore)

const slidersStore = useDoNotMissStore()
const { doNotMiss } = storeToRefs(slidersStore)

const loadContest = useLoadRecordContest()

const {
  arianeRoute,
  header,
  endDate,
  hasArticle,
  defaultArticle,
  defaultContestSocial,
} = useContest({ contest: recordContest, loader: loadContest })

const displayParticipation = ref(false)

const isImageContest = computed(() => {
  return recordContest.value?.media_type === 0
})

const isVideoContest = computed(() => {
  return recordContest.value?.media_type === 1
})

const isScoreContest = computed(() => {
  return recordContest.value?.type === 0
})

const isRunContest = computed(() => {
  return recordContest.value?.type === 1
})

const isRunSecondsContest = computed(() => {
  return !recordContest.value?.milliseconds
})

const isSoloContest = computed(() => {
  return recordContest.value?.participant_number === 1
})

const activityWording = computed(() => {
  return isScoreContest.value ? 'score' : 'temps'
})

const isEndedContest = computed(() => {
  return (
    !!recordContest.value?.contest_end &&
    isBefore(new Date(recordContest.value.contest_end), new Date())
  )
})

const isLockedRanking = computed(() => {
  return recordContest.value?.already_attribution_points
})

const nav = computed(() => {
  const nav = []

  if (recordContest.value?.gifts?.length) {
    nav.push({
      label: 'à gagner',
      ref: 'a-gagner',
    })
  }

  if (hasArticle) {
    nav.push({
      label: 'présentation',
      ref: 'presentation',
    })
  }

  if (recordContest.value?.how_to?.length && countdown(endDate.value) >= 0) {
    nav.push({
      label: 'Comment participer ?',
      ref: 'comment-participer',
    })
  }

  nav.push({
    label: 'Classement',
    ref: 'ranking',
  })

  return nav
})

const article = computed(() => {
  return {
    ...defaultArticle.value,
    type: 'recordContest',
  }
})

const contestSocial = computed(() => {
  return {
    ...defaultContestSocial.value,
    type: 'recordContest',
  }
})

const handleChangeDisplay = (state: boolean) => {
  if (state) {
    document.body.classList.add('body--block-scroll')
  } else {
    document.body.classList.remove('body--block-scroll')
  }

  displayParticipation.value = state
}

const fetchPost = async (slug?: string) => {
  await recordContestsStore.fetchRecordContestPost({
    slug,
  })

  handleChangeDisplay(true)
}

const route = useRoute()

const approveInvitation = async (slug: string) => {
  const { data, error } = await useCustomFetch(
    `${API.GET__SECURE_RECORDCONTEST_INVITATION.replace('$slug', slug)}/1`,
  )

  const rawData = unref(data)
  if (error.value || !rawData) {
    return logError(
      'from contest page',
      "Validation impossible, veuillez contacter un membre de l'équipe WAP",
      error,
    )
  }

  return loadContest(route)
}

const disapproveInvitation = async (slug: string) => {
  const { data, error } = await useCustomFetch(
    `${API.GET__SECURE_RECORDCONTEST_INVITATION.replace('$slug', slug)}/0`,
  )

  const rawData = unref(data)
  if (error.value || !rawData) {
    return logError(
      'from contest page',
      "Refus impossible, veuillez contacter un membre de l'équipe WAP",
      error,
    )
  }

  return loadContest(route)
}

provide(activityWordingKey, activityWording)
provide(isImageContestKey, isImageContest)
provide(isVideoContestKey, isVideoContest)
provide(isScoreContestKey, isScoreContest)
provide(isRunContestKey, isRunContest)
provide(isRunSecondsContestKey, isRunSecondsContest)
provide(isSoloContestKey, isSoloContest)
provide(isEndedContestKey, isEndedContest)
provide(isLockedRankingKey, isLockedRanking)
provide(injectedFetchPostKey, fetchPost)
provide(injectedInvitationApproveKey, approveInvitation)
provide(injectedInvitationDisapproveKey, disapproveInvitation)

const { authenticated } = useAuthenticated()

watch(authenticated, (newValue) => {
  if (newValue) {
    loadContest(route)
  }
})

onMounted(async () => {
  nextTick(() => {
    if (route.hash) {
      const hash = route.hash
      const anchor = document.querySelector(hash)

      if (anchor) {
        window.scrollTo({
          top: anchor.getBoundingClientRect().top + window.scrollY - 170,
          behavior: 'smooth',
        })
      }
    }
  })
})
</script>

<style lang="scss" scoped>
.content-page {
  padding-bottom: 80px;

  @media screen and (max-width: 700px) {
    padding-bottom: 40px;
  }

  .hasNoArticle {
    margin-top: 60px;
  }

  .post-container {
    position: relative;

    .favicon {
      position: absolute;
      top: -4.4rem;
      left: 8rem;
      z-index: 2;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 8.4rem;
      height: 8.4rem;
      background: $white;
      border-radius: 4.2rem;

      @media screen and (max-width: 430px) {
        left: 2.5rem;
        width: 7rem;
        height: 7rem;
        border-radius: 3.5rem;
      }

      img {
        width: 5.6rem;
        height: 3.8rem;
        object-fit: contain;
      }
    }
  }

  hr {
    height: 1px;
    margin: 8rem 8rem 0;
    background-color: rgba(#000, 0.2);
    border: 0;
  }

  .mention {
    margin-top: 10rem;
  }
}

@media screen and (max-width: 1024px) {
  .content-page {
    hr.separator {
      display: none;
    }
  }
}

@media screen and (max-width: 700px) {
  .content-page {
    .other {
      margin-top: 5rem;
      padding-right: 0;
      padding-left: 0;
    }

    .mention {
      margin: 2.5em;
    }
  }
}
</style>

<style>
.body--block-scroll {
  width: 100vw;
  overflow: hidden;
}

/* stylelint-disable-next-line selector-max-id */
#comments {
  scroll-margin-top: 170px;
}
</style>
